import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { formatCountdown } from '@/lib/idleClock'

interface Props {
  open: boolean
  msRemaining: number
  onStaySignedIn: () => void
  onSignOut: () => void
}

/**
 * The "you are about to be signed out" warning.
 *
 * One rule here is easy to get wrong and worth stating: **dismissing this
 * dialog is not the same as staying signed in.** Closing it with Escape, the X,
 * or a click outside leaves the countdown running and the session still ends on
 * time. Only the explicit button resets the clock.
 *
 * That is why `onOpenChange` does NOT call `onStaySignedIn`: the shadcn dialog
 * reports every dismissal through it, so wiring the two together would let a
 * stray click cancel the timeout. The companion half of the rule lives in
 * `useIdleSession`, which stops treating pointer and key events as activity for
 * as long as this is showing — otherwise merely reaching for the button would
 * reset the clock before it was pressed.
 */
export function IdleWarningDialog({ open, msRemaining, onStaySignedIn, onSignOut }: Props) {
  return (
    <Dialog open={open} onOpenChange={() => undefined}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>Your session is about to end</DialogTitle>
          <DialogDescription>
            You have been inactive for a while. You will be signed out in{' '}
            <span className='font-medium tabular-nums text-foreground'>
              {formatCountdown(msRemaining)}
            </span>
            .
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className='gap-2 sm:gap-2'>
          <Button variant='outline' onClick={onSignOut}>
            Sign out now
          </Button>
          <Button onClick={onStaySignedIn}>Stay signed in</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
